import { useId } from "react";
import t from "@/lang/lang";
import { EntityId } from "@/shared/types/entity-id";
import { useSamplesDeviceListQuery } from "@/services/backend/samples/api/device-api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Field,
  FieldDescription,
  FieldLabel,
} from "@/shared/components/ui/field";
import { FieldErrors, FieldProps } from "@/shared/components/field/field";

/** See `noDefault` of the default lab field. */
const noDefault = "none";

/**
 * Selects the device air samples are taken with by default. "Kein
 * Standard" leaves the device to be chosen per sample.
 */
export function DefaultDeviceField({
  deviceId,
  onDeviceIdChange,
  label = t("Standardgerät"),
  description = t("Neue Proben werden mit diesem Gerät genommen."),
  errors,
  fieldProps,
  disabled,
}: {
  deviceId: EntityId;
  onDeviceIdChange: (deviceId: EntityId) => void;
  disabled?: boolean;
} & FieldProps) {
  const id = useId();
  const { data } = useSamplesDeviceListQuery({});

  return (
    <Field {...fieldProps}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Select
        value={deviceId ?? noDefault}
        onValueChange={(v) => onDeviceIdChange(v === noDefault ? null : v)}
        disabled={disabled || !data}
      >
        <SelectTrigger id={id} aria-invalid={errors && errors.length > 0}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={noDefault}>{t("Kein Standard")}</SelectItem>
          {(data?.devices ?? []).map((d) => (
            <SelectItem value={d.id} key={d.id}>
              {d.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {description && <FieldDescription>{description}</FieldDescription>}
      <FieldErrors errors={errors} />
    </Field>
  );
}
